import { cmToIn, inToFtAndIn, kgToPd } from "../../questionnaire/utils";
import { AyurmaStrings } from "../i18n/ayurma";
import { ReportLang } from "../i18n/reportLang";
import { formatAmount } from "./format";

/**
 * `data.CUSTOM_DATA` as served for the white-label RMR report.
 *
 * These are the questionnaire answers verbatim — the same codes `dataJSON.json`
 * enumerates for the `gender`, `mass_measurement_unit`, `exercise_frequency` and
 * `primary_fitness_goal` questions — so the maps below are keyed by those codes
 * rather than by any report-side numbering. Every field is optional: the block is
 * absent on older payloads and on clients who skipped the questionnaire.
 *
 * `current_weight` and `height` are always metric (kg / cm) regardless of the unit
 * codes, matching how the platform stores them everywhere else; the unit code is
 * the client's *display* preference, which is what the converters below apply.
 */
export type WhiteLabelCustomData = {
  current_weight?: number | string | null;
  height?: number | string | null;
  /** 1 = metric (kg), 2 = imperial (lbs). */
  mass_measurement_unit?: number | null;
  /** 1 = metric (cm), 2 = imperial (ft/in). */
  length_measurement_unit?: number | null;
  /** Sessions per week, 0-7. */
  exercise_frequency?: number | null;
  primary_fitness_goal?: number | null;
  gender?: number | null;
};

/** The questionnaire's imperial code, shared by both measurement-unit questions. */
const IMPERIAL = 2;

type PatientInfo = AyurmaStrings["patientInfo"];

const GENDER_KEYS: Record<number, keyof PatientInfo["genders"]> = {
  1: "male",
  2: "female",
};

const GOAL_KEYS: Record<number, keyof PatientInfo["goals"]> = {
  1: "muscleDevelopment",
  2: "strengthDevelopment",
  3: "endurancePerformance",
  4: "longevity",
  5: "bodyRecomposition",
  6: "dynamicPerformance",
};

/**
 * Reads a numeric field off the payload.
 *
 * The values arrive as JSON numbers today, but the report payload types several
 * neighbouring amounts as strings, so a numeric string is accepted too. Anything
 * else — null, "", an absent block — becomes undefined and the caller falls back
 * to a blank write-in rule.
 */
const numeric = (raw: unknown): number | undefined => {
  if (raw === null || raw === undefined || raw === "") return undefined;
  const value = Number(raw);
  return Number.isFinite(value) ? value : undefined;
};

/** Localized gender, e.g. "Female". Undefined when the code is absent or unknown. */
export const genderLabel = (
  custom: WhiteLabelCustomData | undefined,
  t: PatientInfo
): string | undefined => {
  const code = numeric(custom?.gender);
  const key = code === undefined ? undefined : GENDER_KEYS[code];
  return key ? t.genders[key] : undefined;
};

/**
 * Localized weight in the client's preferred unit, e.g. "78 kg" / "172 lbs".
 *
 * kgToPd rounds, which is the right precision for a printed report; the metric
 * value keeps whatever precision the payload sent (78.0000 prints as "78").
 */
export const weightLabel = (
  custom: WhiteLabelCustomData | undefined,
  t: PatientInfo,
  lang: ReportLang
): string | undefined => {
  const kg = numeric(custom?.current_weight);
  if (kg === undefined) return undefined;
  return numeric(custom?.mass_measurement_unit) === IMPERIAL
    ? formatAmount(kgToPd(kg), lang, t.units.lbs)
    : formatAmount(kg, lang, t.units.kg);
};

/**
 * Localized height in the client's preferred unit, e.g. "183 cm" / "6 ft 0 in".
 *
 * Imperial goes to feet-and-inches rather than bare inches, which is how the
 * platform presents height everywhere a person reads it (formatHeightDisplay).
 */
export const heightLabel = (
  custom: WhiteLabelCustomData | undefined,
  t: PatientInfo,
  lang: ReportLang
): string | undefined => {
  const cm = numeric(custom?.height);
  if (cm === undefined) return undefined;
  if (numeric(custom?.length_measurement_unit) !== IMPERIAL) {
    return formatAmount(cm, lang, t.units.cm);
  }
  const { value1: feet, value2: inches } = inToFtAndIn(cmToIn(cm));
  return `${feet} ${t.units.ft} ${inches} ${t.units.in}`;
};

/**
 * Activity level, rendered from the weekly session count, e.g. "3x / week".
 *
 * The count is substituted into the dictionary template so the translation
 * controls word order — Chinese puts it in the middle ("每周 3 次").
 */
export const activityLevelLabel = (
  custom: WhiteLabelCustomData | undefined,
  t: PatientInfo,
  lang: ReportLang
): string | undefined => {
  const count = numeric(custom?.exercise_frequency);
  if (count === undefined) return undefined;
  if (count <= 0) return t.exerciseFrequency.none;
  return t.exerciseFrequency.perWeek.replace(
    "{count}",
    formatAmount(count, lang)
  );
};

/** Localized primary fitness goal. Undefined when the code is absent or unknown. */
export const goalLabel = (
  custom: WhiteLabelCustomData | undefined,
  t: PatientInfo
): string | undefined => {
  const code = numeric(custom?.primary_fitness_goal);
  const key = code === undefined ? undefined : GOAL_KEYS[code];
  return key ? t.goals[key] : undefined;
};
